import React, { useState, useEffect } from 'react';
import { db } from './firebaseConfig';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { TextField, Button, Box, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';

// Función para obtener la fecha local en formato YYYY-MM-DD
const obtenerFechaLocal = () => {
  const fecha = new Date();
  const year = fecha.getFullYear();
  const month = (fecha.getMonth() + 1).toString().padStart(2, '0');
  const day = fecha.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ReporteVentas = () => {
  const [fechaSeleccionada, setFechaSeleccionada] = useState(obtenerFechaLocal());
  const [ventas, setVentas] = useState([]);
  const [totalVentasCredito, setTotalVentasCredito] = useState(0);
  const [totalVentasContado, setTotalVentasContado] = useState(0);
  const [totalGastos, setTotalGastos] = useState(0);
  const [totalAbonosEmpleados, setTotalAbonosEmpleados] = useState(0);
  const [totalAbonosClientes, setTotalAbonosClientes] = useState(0);
  const [totalEfectivo, setTotalEfectivo] = useState(0);

  const obtenerNombreBodega = async (bodegaId) => {
    try {
      const bodegaRef = doc(db, 'bodegas', bodegaId);
      const bodegaDoc = await getDoc(bodegaRef);
      return bodegaDoc.exists() ? bodegaDoc.data().nombre : 'Bodega no encontrada';
    } catch (error) {
      console.error('Error al obtener el nombre de la bodega:', error);
      return 'Bodega no encontrada';
    }
  };

  const obtenerVentasPorFecha = async () => {
    try {
      const ventasRef = collection(db, 'ventas');
      const q = query(ventasRef, where('fecha', '==', fechaSeleccionada));
      const querySnapshot = await getDocs(q);

      let totalCredito = 0;
      let totalContado = 0;
      let ventasFiltradas = [];

      for (const ventaDoc of querySnapshot.docs) {
        const venta = ventaDoc.data();
        const nombreBodega = await obtenerNombreBodega(venta.bodegaId);

        if (venta.tipoVenta === 'Crédito') {
          totalCredito += venta.total;
        } else {
          totalContado += venta.total;
        }

        const detallesSnapshot = await getDocs(collection(db, `ventas/${ventaDoc.id}/detalles`));
        const detallesVenta = detallesSnapshot.docs.map((detalleDoc) => detalleDoc.data());

        ventasFiltradas.push({
          ...venta,
          detalles: detallesVenta,
          nombreBodega: nombreBodega,
        });
      }

      setVentas(ventasFiltradas);
      setTotalVentasCredito(totalCredito);
      setTotalVentasContado(totalContado);

      await obtenerGastosYAbonos(totalContado);
    } catch (error) {
      console.error('Error al obtener las ventas: ', error);
    }
  };

  const obtenerGastosYAbonos = async (totalContado) => {
    try {
      const gastosSnapshot = await getDocs(
        query(collection(db, 'gastos'), where('fecha', '==', fechaSeleccionada))
      );
      const totalGastos = gastosSnapshot.docs.reduce((total, gastoDoc) => total + gastoDoc.data().monto, 0);
      setTotalGastos(totalGastos);

      const abonosEmpleadosSnapshot = await getDocs(
        query(collection(db, 'abono_empleados'), where('fecha', '==', fechaSeleccionada))
      );
      const totalAbonosEmpleados = abonosEmpleadosSnapshot.docs.reduce((total, abonoDoc) => total + abonoDoc.data().monto, 0);
      setTotalAbonosEmpleados(totalAbonosEmpleados);

      const abonosClientesSnapshot = await getDocs(
        query(collection(db, 'abonos'), where('fecha', '==', fechaSeleccionada), where('estado', '==', 'Aprobado'))
      );
      const totalAbonosClientes = abonosClientesSnapshot.docs.reduce((total, abonoDoc) => total + abonoDoc.data().monto, 0);
      setTotalAbonosClientes(totalAbonosClientes);

      const efectivo = totalContado - totalGastos - totalAbonosEmpleados + totalAbonosClientes;
      setTotalEfectivo(efectivo);
    } catch (error) {
      console.error('Error al obtener gastos y abonos: ', error);
    }
  };

  return (
    <Box
      sx={{
        mt: 4,
        p: 3,
        backgroundColor: 'white',
        borderRadius: 2,
        boxShadow: 3,
        maxWidth: '800px',
        margin: '0 auto',
        width: '100%',
      }}
    >
      <h2>Reporte de Ventas por Fecha</h2>

      <TextField
        fullWidth
        label="Fecha"
        type="date"
        value={fechaSeleccionada}
        onChange={(e) => setFechaSeleccionada(e.target.value)}
        margin="normal"
      />

      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={obtenerVentasPorFecha}
        sx={{ mt: 2 }}
      >
        Consultar Ventas
      </Button>

      <h3>Ventas del {fechaSeleccionada}</h3>

      <Table sx={{ mt: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Bodega</TableCell>
            <TableCell>Tipo de Maíz</TableCell>
            <TableCell>Cantidad</TableCell>
            <TableCell>Precio Venta</TableCell> {/* Nueva columna de Precio */}
            <TableCell>Tipo de Venta</TableCell>
            <TableCell>Subtotal</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ventas.length > 0 ? (
            ventas.map((venta, index) => (
              <React.Fragment key={index}>
                {venta.detalles.map((detalle, detalleIndex) => (
                  <TableRow key={detalleIndex}>
                    <TableCell>{venta.nombreBodega}</TableCell>
                    <TableCell>{detalle.nombre}</TableCell>
                    <TableCell>{detalle.cantidad}</TableCell>
                    <TableCell>
                      {detalle.precioVenta != null ? `Q. ${detalle.precioVenta}` : "N/A"}
                    </TableCell> {/* Mostrar precio de venta */}
                    <TableCell>{venta.tipoVenta}</TableCell>
                    <TableCell>Q. {detalle.subtotal.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center">
                No se encontraron ventas para esta fecha
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <h3>Total ventas al Contado: <span style={{ color: 'blue' }}>Q. {totalVentasContado.toFixed(2)}</span></h3>
      <h3>Total ventas al Crédito: <span style={{ color: 'red' }}>Q. {totalVentasCredito.toFixed(2)}</span></h3>
      <h3>Total de ventas del día: <span>Q. {(totalVentasContado + totalVentasCredito).toFixed(2)}</span></h3>
      <h3>Total Gastos Varios: <span style={{ color: 'red' }}>Q. {totalGastos.toFixed(2)}</span></h3>
      <h3>Total Abonos Empleados: <span style={{ color: 'red' }}>Q. {totalAbonosEmpleados.toFixed(2)}</span></h3>
      <h3>Total Abonos Clientes: <span style={{ color: 'blue' }}>Q. {totalAbonosClientes.toFixed(2)}</span></h3>
      <h3>Total Efectivo: <span style={{ color: 'blue' }}>Q. {totalEfectivo.toFixed(2)}</span></h3>
    </Box>
  );
};

export default ReporteVentas;
